// javascript
import { useState, useEffect, useRef } from 'react';
import { toast } from 'react-toastify';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import EventIcon from '@mui/icons-material/Event';
import TimerIcon from '@mui/icons-material/Timer';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import DeleteIcon from '@mui/icons-material/Delete';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DownloadIcon from '@mui/icons-material/Download';
import PercentIcon from '@mui/icons-material/Percent';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningIcon from '@mui/icons-material/Warning';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import cambioEstadoService from '../services/cambioEstadoService';
import { subirArchivo, obtenerArchivosPorTramite, descargarArchivo, eliminarArchivo } from '../services/archivoService';

// Función auxiliar para obtener la fecha actual en formato YYYY-MM-DD
const obtenerFechaActual = () => {
    const hoy = new Date();
    return hoy.toISOString().split('T')[0];
};

function GestorEstadoEsperaDictamenCMC({ tramite, onEstadoCambiado }) {
    const [cargando, setCargando] = useState(false);
    const [mostrarFormularioDictamen, setMostrarFormularioDictamen] = useState(false);
    const [historialEstados, setHistorialEstados] = useState([]);
    const [cargandoHistorial, setCargandoHistorial] = useState(true);
    const [infoApelacion, setInfoApelacion] = useState(null);
    const [archivos, setArchivos] = useState([]);
    const [cargandoArchivos, setCargandoArchivos] = useState(false);
    const [subiendoArchivos, setSubiendoArchivos] = useState(false);
    const [dragActive, setDragActive] = useState(false);
    const fileInputRef = useRef(null);

    // Estados del formulario
    const [datosDictamen, setDatosDictamen] = useState({
        resultado: '',
        fecha: obtenerFechaActual(),
        porcentaje: ''
    });

    // Tiempo estimado de respuesta (configurable, por defecto 45 días)
    const TIEMPO_ESTIMADO_RESPUESTA_DIAS = 45;
    const [diasTranscurridos, setDiasTranscurridos] = useState(null);
    const [alertaTiempo, setAlertaTiempo] = useState(false);

    useEffect(() => {
        cargarHistorial();
        cargarArchivos();
    }, [tramite.idTramite]);

    useEffect(() => {
        if (infoApelacion && infoApelacion.fechaPresentacionCMC) {
            calcularTiempoTranscurrido();
            const interval = setInterval(() => {
                calcularTiempoTranscurrido();
            }, 86400000); // Actualizar cada día

            return () => clearInterval(interval);
        }
    }, [infoApelacion]);

    const calcularTiempoTranscurrido = () => {
        if (!infoApelacion || !infoApelacion.fechaPresentacionCMC) return;

        try {
            const fechaPresentacion = parsearFechaDesdeTexto(infoApelacion.fechaPresentacionCMC);
            if (!fechaPresentacion) return;

            const hoy = new Date();
            hoy.setHours(0, 0, 0, 0);
            fechaPresentacion.setHours(0, 0, 0, 0);

            const diffTime = hoy - fechaPresentacion;
            const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));

            setDiasTranscurridos(diffDays);
            setAlertaTiempo(diffDays > TIEMPO_ESTIMADO_RESPUESTA_DIAS);
        } catch (error) {
            console.error("Error al calcular tiempo transcurrido:", error);
        }
    };

    const parsearFechaDesdeTexto = (fechaTexto) => {
        try {
            const meses = {
                'enero': 0, 'febrero': 1, 'marzo': 2, 'abril': 3,
                'mayo': 4, 'junio': 5, 'julio': 6, 'agosto': 7,
                'septiembre': 8, 'octubre': 9, 'noviembre': 10, 'diciembre': 11
            };
            const partes = fechaTexto.toLowerCase().split(' ');
            if (partes.length >= 3) {
                const dia = parseInt(partes[0]);
                const mes = meses[partes[2]];
                const año = parseInt(partes[4] || partes[partes.length - 1]);
                if (!isNaN(dia) && mes !== undefined && !isNaN(año)) {
                    return new Date(año, mes, dia);
                }
            }
            // Intentar parsear como fecha ISO
            const fechaISO = new Date(fechaTexto);
            if (!isNaN(fechaISO.getTime())) {
                return fechaISO;
            }
        } catch (e) {
            console.error("Error al parsear fecha:", e);
        }
        return null;
    };

    const cargarHistorial = async () => {
        setCargandoHistorial(true);
        try {
            const historial = await cambioEstadoService.getHistorialEstados(tramite.idTramite);
            setHistorialEstados(historial);

            // Buscar el cambio de estado que contiene la información de la apelación CMC
            const cambioApelacion = historial
                .filter(c => c.comentario && c.comentario.includes('Apelación CMC registrada'))
                .pop();

            if (cambioApelacion && cambioApelacion.comentario) {
                parsearInfoApelacion(cambioApelacion.comentario);
            }
        } catch (error) {
            console.error("Error al cargar historial:", error);
            toast.error("No se pudo cargar la información de la apelación.");
        } finally {
            setCargandoHistorial(false);
        }
    };

    const cargarArchivos = async () => {
        setCargandoArchivos(true);
        try {
            const archivosBackend = await obtenerArchivosPorTramite(tramite.idTramite);
            const archivosMapeados = archivosBackend.map(archivo => ({
                id: `backend_${archivo.idArchivo}`,
                idArchivo: archivo.idArchivo,
                nombre: archivo.nombre,
                tamaño: (archivo.tamaño / (1024 * 1024)).toFixed(2) + " MB",
                estado: "subido",
                esDelBackend: true
            }));
            setArchivos(archivosMapeados);
        } catch (error) {
            console.error("Error cargando archivos:", error);
        } finally {
            setCargandoArchivos(false);
        }
    };

    const parsearInfoApelacion = (comentario) => {
        const info = {
            fechaNotificacion: null,
            porcentaje: null,
            fechaLimite: null,
            motivo: null,
            fechaPresentacionCMC: null
        };

        // Extraer fecha de notificación
        const fechaNotMatch = comentario.match(/Fecha de notificación del dictamen: (.+?)(?:\n|$)/);
        if (fechaNotMatch) {
            info.fechaNotificacion = fechaNotMatch[1].trim();
        }

        // Extraer porcentaje
        const porcentajeMatch = comentario.match(/Porcentaje de incapacidad: ([\d.]+)%/);
        if (porcentajeMatch) {
            info.porcentaje = parseFloat(porcentajeMatch[1]);
        }

        // Extraer fecha límite
        const fechaLimiteMatch = comentario.match(/Fecha límite para apelar: (.+?)(?:\n|$)/);
        if (fechaLimiteMatch) {
            info.fechaLimite = fechaLimiteMatch[1].trim();
        }


        // Extraer fecha de presentación CMC
        const fechaPresentacionMatch = comentario.match(/Fecha de presentación CMC: (.+?)(?:\n|$)/);
        if (fechaPresentacionMatch) {
            info.fechaPresentacionCMC = fechaPresentacionMatch[1].trim();
        }

        // Extraer motivo
        const motivoMatch = comentario.match(/Motivo: (.+?)(?:\nArchivos|$)/);
        if (motivoMatch) {
            info.motivo = motivoMatch[1].trim();
        }

        setInfoApelacion(info);
    };

    const handleSeleccionarArchivos = (event) => {
        const files = Array.from(event.target.files || []);
        if (files.length === 0) return;

        const nuevosArchivos = files.map(file => ({
            id: `local_${Date.now()}_${Math.random()}`,
            file,
            nombre: file.name,
            tamaño: (file.size / (1024 * 1024)).toFixed(2) + " MB",
            estado: "pendiente",
            esDelBackend: false
        }));

        setArchivos(prev => [...prev, ...nuevosArchivos]);
        if (event.target) event.target.value = "";
    };

    const handleDrag = (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (e.type === "dragenter" || e.type === "dragover") setDragActive(true);
        else if (e.type === "dragleave") setDragActive(false);
    };

    const handleDrop = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setDragActive(false);
        if (e.dataTransfer.files && e.dataTransfer.files[0]) {
            handleSeleccionarArchivos({ target: { files: e.dataTransfer.files } });
        }
    };

    const handleEliminarArchivo = async (id) => {
        const archivo = archivos.find(a => a.id === id);
        if (!archivo) return;

        if (window.confirm(`¿Seguro que quieres eliminar "${archivo.nombre}"?`)) {
            if (archivo.esDelBackend) {
                try {
                    await eliminarArchivo(archivo.idArchivo);
                } catch (error) {
                    toast.error("Error al eliminar el archivo del servidor.");
                    return;
                }
            }
            setArchivos(prev => prev.filter(a => a.id !== id));
            toast.info(`Archivo "${archivo.nombre}" eliminado.`);
        }
    };

    const handleDescargarArchivo = async (archivo) => {
        if (!archivo.esDelBackend) {
            toast.info("El archivo debe subirse primero para poder descargarlo.");
            return;
        }
        try {
            const response = await descargarArchivo(archivo.idArchivo);
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', archivo.nombre);
            document.body.appendChild(link);
            link.click();
            link.remove();
        } catch (error) {
            toast.error("Error al descargar el archivo.");
        }
    };

    const handleSubirArchivos = async () => {
        const archivosPendientes = archivos.filter(a => a.estado === 'pendiente');
        if (archivosPendientes.length === 0) return [];

        setSubiendoArchivos(true);
        const archivosSubidos = [];
        for (const archivo of archivosPendientes) {
            try {
                const archivoSubido = await subirArchivo(tramite.idTramite, archivo.file);
                archivosSubidos.push(archivoSubido);
                setArchivos(prev => prev.map(item =>
                    item.id === archivo.id ? { ...item, estado: "subido", idArchivo: archivoSubido.idArchivo, esDelBackend: true } : item
                ));
            } catch (error) {
                setArchivos(prev => prev.map(item =>
                    item.id === archivo.id ? { ...item, estado: "error" } : item
                ));
                toast.error(`Error subiendo ${archivo.nombre}`);
            }
        }
        setSubiendoArchivos(false);
        return archivosSubidos;
    };

    const handleConfirmarDictamen = async () => {
        if (!datosDictamen.resultado || !datosDictamen.fecha) {
            toast.error("Debe indicar el resultado y la fecha del dictamen.");
            return;
        }
        if (!datosDictamen.porcentaje || datosDictamen.porcentaje === '') {
            toast.error("Debe indicar el porcentaje de incapacidad del dictamen.");
            return;
        }

        const porcentaje = parseFloat(datosDictamen.porcentaje);
        
        // Validación: Rechazado no puede tener porcentaje >= 66
        if (datosDictamen.resultado === "Rechazado" && porcentaje >= 66) {
            toast.error("❌ Error: Un dictamen Rechazado no puede tener un porcentaje mayor o igual a 66%.");
            return;
        }
        
        // Validación: Aprobado no puede tener porcentaje < 66
        if (datosDictamen.resultado === "Aprobado" && porcentaje < 66) {
            toast.error("❌ Error: Un dictamen Aprobado debe tener un porcentaje mayor o igual a 66%.");
            return;
        }
        const archivosPendientes = archivos.filter(a => a.estado === 'pendiente');
        if (archivosPendientes.length === 0 && archivos.filter(a => a.esDelBackend).length === 0) {
            toast.error("Debe adjuntar el archivo del dictamen.");
            return;
        }

        setCargando(true);
        try {
            // Subir archivos pendientes si hay
            if (archivosPendientes.length > 0) {
                const archivosSubidos = await handleSubirArchivos();
                if (archivosSubidos.length !== archivosPendientes.length) {
                    toast.error("No se pudieron subir todos los archivos. Por favor, intente de nuevo.");
                    throw new Error("Fallo en la subida de archivos");
                }
            }

            const porcentaje = parseFloat(datosDictamen.porcentaje);
            
            const comentario = `Dictamen CMC recibido\nResultado: ${datosDictamen.resultado}\nFecha del dictamen: ${formatearFecha(datosDictamen.fecha)}\nPorcentaje de incapacidad: ${porcentaje}%`;

            // Si el porcentaje es menor a 66%, transicionar a Segunda Apelación (estado 10)
            // Si es >= 66%, el trámite debería completarse
            let siguienteEstado;
            if (porcentaje < 66) {
                siguienteEstado = 10; // Segunda Apelación
                toast.success(`Dictamen CMC cargado. El trámite ha sido movido a "Segunda Apelación" (porcentaje ${porcentaje}% < 66%).`);
            } else {
                siguienteEstado = 8; // Completo
                toast.success(`Dictamen CMC cargado. El trámite ha sido completado (porcentaje ${porcentaje}% >= 66%).`);
            }

            const cambioEstadoData = {
                idEstado: siguienteEstado,
                comentario: comentario
            };

            await cambioEstadoService.cambiarEstado(tramite.idTramite, cambioEstadoData);

            setMostrarFormularioDictamen(false);
            setArchivos([]);
            setDatosDictamen({ resultado: '', fecha: obtenerFechaActual(), porcentaje: '' });
            if (onEstadoCambiado) await onEstadoCambiado();

        } catch (error) {
            console.error("Error al confirmar dictamen CMC:", error);
            toast.error("Error al procesar el dictamen: " + (error.response?.data || error.message));
        } finally {
            setCargando(false);
        }
    };

    const formatearFecha = (fechaString) => {
        if (!fechaString) return '';
        const fecha = new Date(fechaString);
        if (isNaN(fecha.getTime())) return '';
        const fechaLocal = new Date(fecha.valueOf() + fecha.getTimezoneOffset() * 60000);
        return fechaLocal.toLocaleDateString('es-ES', {
            weekday: 'long',
            day: 'numeric',
            month: 'long',
            year: 'numeric'
        });
    };

    const renderFormularioDictamen = () => (
        <div className="card shadow-sm border-0 rounded-4 p-4 mt-4">
            <h5 className="fw-semibold text-primary mb-3">Cargar Dictamen CMC</h5>
            <div className="row g-3">
                <div className="col-md-4">
                    <label className="form-label fw-semibold">Resultado del Dictamen <span className="text-danger">*</span></label>
                    <select className="form-select" value={datosDictamen.resultado} onChange={e => setDatosDictamen({ ...datosDictamen, resultado: e.target.value })} required>
                        <option value="">Seleccionar...</option>
                        <option value="Aprobado">Aprobado</option>
                        <option value="Rechazado">Rechazado</option>
                    </select>
                </div>
                <div className="col-md-4">
                    <label className="form-label fw-semibold">Fecha del Dictamen <span className="text-danger">*</span></label>
                    <input type="date" className="form-control" value={datosDictamen.fecha} onChange={e => setDatosDictamen({ ...datosDictamen, fecha: e.target.value })} required />
                </div>
                <div className="col-md-4">
                    <label className="form-label fw-semibold">Porcentaje de Incapacidad (%) <span className="text-danger">*</span></label>
                    <input
                        type="number"
                        className="form-control"
                        min="0"
                        max="100"
                        step="0.01"
                        value={datosDictamen.porcentaje}
                        onChange={e => setDatosDictamen({ ...datosDictamen, porcentaje: e.target.value })}
                        required
                        placeholder="Ej: 45.5"
                    />
                    {datosDictamen.resultado && datosDictamen.porcentaje && (
                        (datosDictamen.resultado === "Rechazado" && parseFloat(datosDictamen.porcentaje) >= 66) ||
                        (datosDictamen.resultado === "Aprobado" && parseFloat(datosDictamen.porcentaje) < 66)
                    ) && (
                        <small className="text-danger d-block mt-1 fw-semibold">
                            ❌ Inconsistencia: {datosDictamen.resultado === "Rechazado" 
                                ? "Un dictamen Rechazado debe tener menos de 66%" 
                                : "Un dictamen Aprobado debe tener 66% o más"}
                        </small>
                    )}
                </div>
            </div>

            {/* Gestor de Archivos */}
            <div className="mt-4">
                <h6 className="fw-semibold text-primary">Adjuntar Archivo del Dictamen CMC</h6>
                {archivos.length > 0 && (
                    <div className="list-group mb-3">
                        {archivos.map(archivo => (
                            <div key={archivo.id} className="list-group-item d-flex justify-content-between align-items-center">
                                <div>
                                    <AttachFileIcon fontSize="small" className="me-2 text-muted" />
                                    <span className="fw-semibold">{archivo.nombre}</span>
                                    <small className="text-muted ms-2">({archivo.tamaño})</small>
                                    <span className={`badge ms-2 ${archivo.estado === 'subido' ? 'bg-success' : archivo.estado === 'error' ? 'bg-danger' : 'bg-warning text-dark'}`}>
                                        {archivo.estado}
                                    </span>
                                </div>
                                <div>
                                    {archivo.esDelBackend && (
                                        <button
                                            className="btn btn-sm btn-outline-primary me-2"
                                            onClick={() => handleDescargarArchivo(archivo)}
                                            title="Descargar"
                                        >
                                            <DownloadIcon fontSize="small" />
                                        </button>
                                    )}
                                    <button
                                        className="btn btn-sm btn-outline-danger"
                                        onClick={() => handleEliminarArchivo(archivo.id)}
                                        title="Eliminar"
                                    >
                                        <DeleteIcon fontSize="small" />
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
                <div
                    className={`border rounded-3 p-4 text-center cursor-pointer ${dragActive ? "border-primary bg-primary bg-opacity-10" : "border-dashed"}`}
                    onDragEnter={handleDrag}
                    onDragLeave={handleDrag}
                    onDragOver={handleDrag}
                    onDrop={handleDrop}
                    onClick={() => fileInputRef.current?.click()}
                >
                    <input type="file" ref={fileInputRef} onChange={handleSeleccionarArchivos} className="d-none" accept=".pdf,.PDF" />
                    <CloudUploadIcon className="text-muted mb-2" />
                    <p className="mb-0">Arrastra el archivo aquí o haz clic para seleccionar</p>
                    <small className="text-muted">Solo archivos PDF</small>
                </div>
            </div>

            <div className="d-flex justify-content-center gap-3 mt-4">
                <button
                    className="btn btn-primary rounded-pill px-4"
                    onClick={handleConfirmarDictamen}
                    disabled={cargando || subiendoArchivos}
                >
                    {cargando ? 'Guardando...' : 'Confirmar Carga'}
                </button>
                <button
                    className="btn btn-outline-secondary rounded-pill px-4"
                    onClick={() => setMostrarFormularioDictamen(false)}
                    disabled={cargando}
                >
                    Cancelar
                </button>
            </div>
        </div>
    );

    if (cargandoHistorial) {
        return (
            <div className="card shadow-lg border-0 rounded-4 p-4 mb-4">
                <div className="alert alert-info">Cargando información...</div>
            </div>
        );
    }

    return (
        <div className="card shadow-lg border-0 rounded-4 p-4 mb-4">
            <div className="d-flex justify-content-between align-items-center mb-3">
                <h4 className="fw-bold text-info mb-0">
                    <HourglassEmptyIcon className="me-2" />
                    Espera de Dictamen CMC
                </h4>
                <span className="badge bg-info rounded-pill px-3 py-2">
                    <TimerIcon fontSize="small" className="me-1" />
                    Pendiente
                </span>
            </div>

            <div className="alert alert-info border-0 rounded-3">
                <strong>Siguiente Paso:</strong>
                <p className="mb-0 mt-1">Esperar la resolución de la Comisión Médica Central (CMC). Cuando llegue, cargar el dictamen para continuar a 2° Apelación.</p>
            </div>

            {/* Alerta si se supera el tiempo estimado de respuesta */}
            {alertaTiempo && diasTranscurridos !== null && (
                <div className="alert alert-warning border-0 rounded-3 mt-3">
                    <WarningIcon className="me-2" />
                    <strong>⚠️ Alerta de Tiempo:</strong> Han transcurrido <strong>{diasTranscurridos} días</strong> desde la presentación de la apelación CMC 
                    (tiempo estimado de respuesta: {TIEMPO_ESTIMADO_RESPUESTA_DIAS} días). 
                    <br />
                    <small className="mt-2 d-block">Se recomienda realizar seguimiento del estado del trámite ante la CMC.</small>
                </div>
            )}

            {/* Información de la Apelación */}
            {infoApelacion && (
                <div className="mt-3 p-3 bg-light rounded-3 border">
                    <h6 className="fw-semibold text-secondary mb-3">Información de la Apelación CMC</h6>
                    <div className="row g-3">
                        {infoApelacion.fechaPresentacionCMC && (
                            <div className="col-md-6 d-flex align-items-center">
                                <CalendarMonthIcon className="text-primary me-2" />
                                <div>
                                    <small className="text-muted d-block">Fecha de Presentación CMC</small>
                                    <strong className="text-dark">{infoApelacion.fechaPresentacionCMC}</strong>
                                </div>
                            </div>
                        )}
                        {diasTranscurridos !== null && (
                            <div className="col-md-6 d-flex align-items-center">
                                <TimerIcon className="text-primary me-2" />
                                <div>
                                    <small className="text-muted d-block">Días Transcurridos</small>
                                    <strong className={`text-dark ${alertaTiempo ? 'text-warning' : ''}`}>
                                        {diasTranscurridos} días
                                        {alertaTiempo && <small className="ms-2">(Superado el tiempo estimado)</small>}
                                    </strong>
                                </div>
                            </div>
                        )}
                        {infoApelacion.porcentaje !== null && (
                            <div className="col-md-6 d-flex align-items-center">
                                <PercentIcon className="text-primary me-2" />
                                <div>
                                    <small className="text-muted d-block">Porcentaje del Dictamen Anterior</small>
                                    <strong className="text-dark">{infoApelacion.porcentaje}%</strong>
                                </div>
                            </div>
                        )}
                        {infoApelacion.motivo && (
                            <div className="col-12">
                                <small className="text-muted d-block">Motivo de la Apelación</small>
                                <p className="text-dark mb-0" style={{ whiteSpace: 'pre-line' }}>{infoApelacion.motivo}</p>
                            </div>
                        )}
                    </div>
                </div>
            )}

            {mostrarFormularioDictamen ? (
                renderFormularioDictamen()
            ) : (
                <div className="d-flex justify-content-center flex-wrap gap-3 mt-4 pt-3 border-top">
                    <button
                        className="btn btn-primary d-flex align-items-center gap-2 rounded-pill px-4 py-2 fw-semibold"
                        onClick={() => setMostrarFormularioDictamen(true)}
                    >
                        <UploadFileIcon fontSize="small" /> Cargar Dictamen CMC
                    </button>
                </div>
            )}
        </div>
    );
}

export default GestorEstadoEsperaDictamenCMC;

